import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { articles } from "@/mocks/gossip";
import { heroStats } from "@/mocks/site";

const featured = articles[0];

const secondary = [articles[1], articles[2]];

export default function HeroSection() {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/hot?q=${encodeURIComponent(value)}` : "/hot");
  };

  return (
    <section className="relative overflow-hidden border-b border-background-200 bg-background-100">
      <div className="paper-grid absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary-200/50 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-32 left-1/4 h-72 w-72 rounded-full bg-accent-100/60 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto w-full px-4 pb-14 pt-12 md:px-6 md:pb-16 md:pt-16 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary-300 bg-background-50 px-3.5 py-1.5 text-xs font-medium text-primary-700">
              <i className="ri-shield-user-line text-sm" aria-hidden="true" />
              全程匿名 · 24 小时线索征集
            </p>

            <h1 className="font-heading text-[34px] font-bold leading-[1.22] tracking-tight text-foreground-950 md:text-[46px] md:leading-[1.18]">
              <span className="mb-4 block font-sans text-sm font-medium tracking-[0.32em] text-primary-600">
                瓜友会网 GUAYOUHUI
              </span>
              手里有料不敢说
              <br />
              我们替你把它说出来
            </h1>

            <p className="mt-6 max-w-xl text-[15px] leading-8 text-foreground-600">
              深耕线索征集与匿名爆料的老牌瓜料集散地。行程、直播、供应链、职场与校园，
              只要你的线索经得起核实，我们就负责让它被看见——而你的身份，始终留在幕后。
            </p>

            <form onSubmit={handleSearch} className="mt-8 flex max-w-xl flex-col gap-3 sm:flex-row" role="search">
              <div className="relative flex-1">
                <i
                  className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索关键词，如：直播翻车、奖金池"
                  aria-label="搜索瓜料关键词"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3.5 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜一下
              </button>
            </form>

            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
              {heroStats.map((stat) => (
                <div key={stat.label} className="flex items-baseline gap-1.5">
                  <span className="font-heading text-2xl font-bold text-foreground-950">{stat.value}</span>
                  <span className="text-xs text-foreground-500">{stat.unit}</span>
                  <span className="ml-1 text-xs text-foreground-500">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <article className="group relative overflow-hidden rounded-lg border border-background-300 bg-foreground-950">
              <Link to={`/post/${featured.id}`} className="block">
                <div className="h-[300px] w-full overflow-hidden md:h-[380px]">
                  <img
                    src={featured.cover}
                    alt={featured.title}
                    className="h-full w-full object-cover object-top opacity-90 transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div
                  className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/55 to-transparent"
                  aria-hidden="true"
                />
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                  <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                    <span className="flex items-center gap-1 rounded bg-accent-500 px-2.5 py-1 font-medium text-background-50">
                      <i className="ri-flashlight-fill text-xs" aria-hidden="true" />
                      今日头条
                    </span>
                    <span className="rounded bg-background-50/90 px-2.5 py-1 font-medium text-foreground-900">
                      {featured.category}
                    </span>
                    <span className="text-background-200">{featured.date}</span>
                  </div>
                  <h2 className="font-heading text-xl font-bold leading-9 text-background-50 md:text-2xl md:leading-[1.5]">
                    {featured.title}
                  </h2>
                  <p className="mt-3 line-clamp-2 text-sm leading-7 text-background-200">{featured.excerpt}</p>
                </div>
              </Link>
            </article>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {secondary.map((item) => (
                <Link
                  key={item.id}
                  to={`/post/${item.id}`}
                  className="group flex gap-3 rounded-lg border border-background-300 bg-background-50 p-3 transition-colors hover:border-primary-300"
                >
                  <div className="h-[64px] w-[64px] shrink-0 overflow-hidden rounded-md">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="mb-1 text-xs text-primary-700">{item.category}</p>
                    <h3 className="line-clamp-2 text-sm font-medium leading-6 text-foreground-900 transition-colors group-hover:text-primary-700">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}